import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Upload, Search, Image, Video, Mic, Loader2, X,
  ExternalLink, ChevronLeft, ChevronRight, Key, Globe
} from 'lucide-react';
import AudioThumbnail from '../shared/AudioThumbnail';
import toast from 'react-hot-toast';
import { mediaApi, pexelsApi, giphyApi, coverrApi, crawlerApi } from '../../api/client';
import { useEditorStore } from '../../store/editorStore';

export default function MediaPanel() {
  const [tab, setTab] = useState('my-media');

  const tabs = [
    { id: 'my-media', label: 'My Media' },
    { id: 'unsplash', label: 'Photos' },
    { id: 'videos', label: 'Videos' },
    { id: 'giphy', label: 'GIFs' },
    { id: 'stickers', label: 'Stickers' },
    { id: 'crawl', label: 'Crawl' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Tab switcher — 2 rows */}
      <div className="grid grid-cols-3 shrink-0 p-1.5 gap-1" style={{ borderBottom: '1px solid var(--t-border)' }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="py-1.5 text-xs font-medium rounded-md transition-colors"
            style={tab === t.id
              ? { background: 'var(--brand-primary)', color: '#fff' }
              : { background: 'var(--t-panel)', color: 'var(--t-muted)' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'my-media' && <MyMediaTab />}
      {tab === 'unsplash' && <UnsplashTab />}
      {tab === 'videos' && <VideosTab />}
      {tab === 'giphy' && <GiphyTab />}
      {tab === 'stickers' && <StickersTab />}
      {tab === 'crawl' && <CrawlTab />}
    </div>
  );
}

// ── My Media tab ─────────────────────────────────────────────

function MyMediaTab() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const story = useEditorStore((s) => s.story);
  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAssets(); }, [typeFilter, search]);

  async function loadAssets() {
    setLoading(true);
    try {
      const result = await mediaApi.list({
        // storyId: story?.id,
        type: typeFilter === 'all' ? undefined : typeFilter,
        search: search || undefined,
      });
      const items = result?.items ?? result ?? [];
      setAssets(items.map((a) => ({ ...a, id: a.id ?? a._id })));
    } catch {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(files) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await mediaApi.upload(file, story?.id);
      }
      await loadAssets();
      toast.success(`${files.length} file(s) uploaded!`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function handleInsert(asset) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    if (asset.type === 'image') {
      addLayer(activePageId, { type: 'image', mediaId: asset.id, src: asset.url, alt: asset.altText || '', objectFit: 'cover' }, { x: 0, y: 0, width: 100, height: 50 });
    } else if (asset.type === 'video') {
      addLayer(activePageId, { type: 'video', mediaId: asset.id, src: asset.url, poster: asset.posterUrl, autoplay: true, loop: true, muted: true }, { x: 0, y: 0, width: 100, height: 100 });
    } else if (asset.type === 'audio') {
      addLayer(activePageId, { type: 'audio', mediaId: asset.id, src: asset.url, autoplay: true, loop: true, showIcon: true }, { x: 5, y: 5, width: 20, height: 10 });
    }
    toast.success(`${asset.type} added`);
  }

  return (
    <>
      <div className="p-2 shrink-0" style={{ borderBottom: '1px solid var(--t-border)' }}>
        {/* Search */}
        <div className="relative mb-2">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search uploads…" className="input-field pl-6 text-xs py-1" />
          {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X size={11} style={{ color: 'var(--t-muted)' }} /></button>}
        </div>
        {/* Type filter */}
        <div className="flex gap-1">
          {(['all', 'image', 'video', 'audio']).map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`flex-1 py-0.5 text-xs rounded capitalize transition-colors ${typeFilter === t ? 'bg-indigo-600 text-white' : ''}`}
              style={typeFilter !== t ? { background: 'var(--t-panel)', color: 'var(--t-muted)' } : {}}>{t}</button>
          ))}
        </div>
      </div>

      {/* Upload drop zone */}
      <div className="mx-2 mt-2 shrink-0 border-2 border-dashed rounded-lg p-3 text-center
                      hover:border-indigo-500 transition-colors cursor-pointer"
           style={{ borderColor: 'var(--t-border2)' }}
           onClick={() => fileInputRef.current?.click()}
           onDragOver={(e) => e.preventDefault()}
           onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}>
        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,audio/*"
          className="hidden" onChange={(e) => handleUpload(e.target.files)} />
        {uploading
          ? <div className="flex items-center justify-center gap-2 text-indigo-400"><Loader2 size={13} className="animate-spin" /><span className="text-xs">Uploading…</span></div>
          : <><Upload size={14} className="mx-auto mb-1" style={{ color: 'var(--t-muted)' }} /><p className="text-xs" style={{ color: 'var(--t-muted)' }}>Drop files or click to upload</p></>}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? <div className="flex justify-center py-8"><Loader2 size={15} className="animate-spin text-indigo-500" /></div>
          : assets.length === 0 ? (
            <div className="text-center py-10">
              <Upload size={22} className="mx-auto mb-2" style={{ color: 'var(--t-muted)' }} />
              <p className="text-xs" style={{ color: 'var(--t-muted)' }}>No uploads yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {assets.map((asset) => (
                <button key={asset.id} onClick={() => handleInsert(asset)}
                  className="relative aspect-square rounded-lg overflow-hidden hover:ring-2 hover:ring-indigo-500 transition-all group"
                  style={{ background: 'var(--t-panel)' }} title={asset.originalFilename}>
                  {asset.type === 'image' ? (
                    <img src={asset.thumbnailUrl || asset.url} alt={asset.altText || ''} className="w-full h-full object-cover" loading="lazy" />
                  ) : asset.type === 'video' ? (
                    <div className="w-full h-full relative" style={{ background: '#18181b' }}>
                      <video src={asset.url} className="w-full h-full object-cover" preload="metadata" muted playsInline />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div style={{ background: 'rgba(0,0,0,0.45)', borderRadius: '50%', padding: 3, display: 'flex' }}>
                          <Video size={12} className="text-white" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <AudioThumbnail filename={asset.originalFilename} size="sm" />
                  )}
                  <div className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/20 transition-colors" />
                  <div className="absolute bottom-1 right-1 bg-black/60 rounded px-1 py-0.5">
                    {asset.type === 'image' ? <Image size={8} className="text-white" />
                      : asset.type === 'video' ? <Video size={8} className="text-white" />
                      : <Mic size={8} className="text-white" />}
                  </div>
                </button>
              ))}
            </div>
          )}
      </div>
    </>
  );
}

// ── Unsplash tab ─────────────────────────────────────────────

function UnsplashTab() {
  const [photos, setPhotos] = useState([]);
  const [query, setQuery] = useState('');
  const [draftQuery, setDraftQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [inserting, setInserting] = useState(null);

  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  const fetchPhotos = useCallback(async (q, p) => {
    setLoading(true);
    setApiError(null);
    try {
      const result = q
        ? await pexelsApi.search(q, p)
        : await pexelsApi.popular(p);
      setPhotos(result.photos);
      setTotalPages(result.totalPages);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPhotos(query, page); }, [query, page, fetchPhotos]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
  }

  async function handleInsert(photo) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    setInserting(photo.id);
    try {
      addLayer(activePageId, {
        type: 'image',
        src: photo.regular,
        alt: photo.alt,
        objectFit: 'cover',
      }, { x: 0, y: 0, width: 100, height: 60 });
      toast.success('Photo added to canvas');
    } finally {
      setInserting(null);
    }
  }

  const isNoKey = apiError?.includes('PEXELS_API_KEY');

  return (
    <>
      <form onSubmit={handleSearch} className="p-2 shrink-0" style={{ borderBottom: '1px solid var(--t-border)' }}>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} />
          <input
            type="text"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            placeholder="Search free photos…"
            className="input-field pl-6 pr-7 text-xs py-1"
          />
          {draftQuery
            ? <button type="button" onClick={() => { setDraftQuery(''); setPage(1); setQuery(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2">
                <X size={11} style={{ color: 'var(--t-muted)' }} />
              </button>
            : <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
                <Search size={11} style={{ color: 'var(--t-muted)' }} />
              </button>}
        </div>
      </form>

      <div className="flex-1 overflow-y-auto">
        {isNoKey ? (
          <ApiKeyBanner
            service="Pexels"
            envVar="PEXELS_API_KEY"
            docsUrl="https://www.pexels.com/api/"
            docsLabel="pexels.com/api"
          />
        ) : apiError ? (
          <div className="m-2 p-3 rounded-lg border text-xs" style={{ borderColor: 'var(--t-border)', color: 'var(--t-muted)' }}>
            {apiError}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={18} className="animate-spin text-indigo-400" />
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-10">
            <Image size={22} className="mx-auto mb-2" style={{ color: 'var(--t-muted)' }} />
            <p className="text-xs" style={{ color: 'var(--t-muted)' }}>No photos found</p>
          </div>
        ) : (
          <>
            <div className="p-2 grid grid-cols-2 gap-1.5">
              {photos.map((photo) => (
                <button
                  key={photo.id}
                  onClick={() => handleInsert(photo)}
                  disabled={inserting === photo.id}
                  className="relative rounded-lg overflow-hidden hover:ring-2 hover:ring-indigo-500 transition-all group cursor-pointer"
                  style={{ aspectRatio: '1/1', background: photo.color || 'var(--t-panel)' }}
                  title={photo.alt}
                >
                  <img src={photo.thumb} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" />
                  {inserting === photo.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Loader2 size={16} className="animate-spin text-white" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-1"
                       style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                    <p className="text-white text-[9px] truncate">{photo.authorName}</p>
                  </div>
                </button>
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => p + 1)} />
            <div className="px-2 pb-2 shrink-0">
              <a href="https://www.pexels.com"
                 target="_blank" rel="noreferrer"
                 className="flex items-center gap-1 text-xs hover:underline"
                 style={{ color: 'var(--t-muted)' }}
                 onClick={(e) => e.stopPropagation()}>
                <ExternalLink size={10} />
                Photos from Pexels
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Videos tab (Coverr) ───────────────────────────────────────

function VideosTab() {
  const [videos, setVideos] = useState([]);
  const [query, setQuery] = useState('');
  const [draftQuery, setDraftQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [inserting, setInserting] = useState(null);

  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  const fetchVideos = useCallback(async (q, p) => {
    setLoading(true);
    setApiError(null);
    try {
      const result = q ? await coverrApi.search(q, p) : await coverrApi.popular(p);
      setVideos(result.videos);
      setTotalPages(result.totalPages);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVideos(query, page); }, [query, page, fetchVideos]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
  }

  function handleInsert(video) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    setInserting(video.id);
    try {
      addLayer(activePageId, {
        type: 'video',
        src: video.hdUrl || video.sdUrl,
        poster: video.thumbnail,
        autoplay: true,
        loop: true,
        muted: true,
      }, { x: 0, y: 0, width: 100, height: 100 });
      toast.success('Video added to canvas');
    } finally {
      setInserting(null);
    }
  }

  const isNoKey = apiError?.includes('COVERR_API_KEY');

  return (
    <>
      <form onSubmit={handleSearch} className="p-2 shrink-0" style={{ borderBottom: '1px solid var(--t-border)' }}>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} />
          <input
            type="text"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            placeholder="Search free videos…"
            className="input-field pl-6 pr-7 text-xs py-1"
          />
          {draftQuery
            ? <button type="button" onClick={() => { setDraftQuery(''); setPage(1); setQuery(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2">
                <X size={11} style={{ color: 'var(--t-muted)' }} />
              </button>
            : <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
                <Search size={11} style={{ color: 'var(--t-muted)' }} />
              </button>}
        </div>
      </form>

      <div className="flex-1 overflow-y-auto">
        {isNoKey ? (
          <ApiKeyBanner
            service="Coverr"
            envVar="COVERR_API_KEY"
            docsUrl="https://coverr.co/api"
            docsLabel="coverr.co/api"
          />
        ) : apiError ? (
          <div className="m-2 p-3 rounded-lg border text-xs" style={{ borderColor: 'var(--t-border)', color: 'var(--t-muted)' }}>
            {apiError}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={18} className="animate-spin text-indigo-400" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-10">
            <Video size={22} className="mx-auto mb-2" style={{ color: 'var(--t-muted)' }} />
            <p className="text-xs" style={{ color: 'var(--t-muted)' }}>No videos found</p>
          </div>
        ) : (
          <>
            <div className="p-2 grid grid-cols-2 gap-1.5">
              {videos.map((video) => (
                <button
                  key={video.id}
                  onClick={() => handleInsert(video)}
                  disabled={inserting === video.id}
                  className="relative rounded-lg overflow-hidden hover:ring-2 hover:ring-indigo-500 transition-all group cursor-pointer"
                  style={{ aspectRatio: '9/16', background: 'var(--t-panel)' }}
                  title={video.title}
                >
                  {video.thumbnail ? (
                    <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video size={22} style={{ color: 'var(--t-muted)' }} />
                    </div>
                  )}
                  {/* Play icon overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                    <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                      <div style={{ width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '10px solid #1f2937', marginLeft: 2 }} />
                    </div>
                  </div>
                  {inserting === video.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Loader2 size={16} className="animate-spin text-white" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 px-1.5 py-1"
                       style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                    <p className="text-white text-[9px] truncate">{video.title}</p>
                  </div>
                </button>
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => p + 1)} />
            <div className="px-2 pb-2 shrink-0">
              <a href="https://coverr.co" target="_blank" rel="noreferrer"
                 className="flex items-center gap-1 text-xs hover:underline"
                 style={{ color: 'var(--t-muted)' }}
                 onClick={(e) => e.stopPropagation()}>
                <ExternalLink size={10} />
                Free videos from Coverr
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── GIFs tab ──────────────────────────────────────────────────

function GiphyTab() {
  return <GiphyBrowser mode="gifs" />;
}

// ── Stickers tab ──────────────────────────────────────────────

const STICKER_CATEGORIES = [
  'Business', 'Marketing', 'Sale', 'Success', 'Growth',
  'Money', 'Team', 'Launch', 'Award', 'Analytics',
];

function StickersTab() {
  const [gifs, setGifs] = useState([]);
  const [query, setQuery] = useState('business');
  const [draftQuery, setDraftQuery] = useState('business');
  const [activeCategory, setActiveCategory] = useState('Business');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [inserting, setInserting] = useState(null);

  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  const fetchStickers = useCallback(async (q, p) => {
    setLoading(true);
    setApiError(null);
    try {
      const result = await giphyApi.searchStickers(q, p);
      setGifs(result.gifs);
      setTotalPages(result.totalPages);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStickers(query, page); }, [query, page, fetchStickers]);

  function handleSearch(e) {
    e.preventDefault();
    const q = draftQuery.trim() || 'business';
    setActiveCategory('');
    setPage(1);
    setQuery(q);
  }

  function handleCategory(cat) {
    setActiveCategory(cat);
    setDraftQuery(cat.toLowerCase());
    setPage(1);
    setQuery(cat.toLowerCase());
  }

  function handleInsert(gif) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    setInserting(gif.id);
    try {
      addLayer(activePageId, {
        type: 'image',
        src: gif.url,
        alt: gif.title,
        objectFit: 'contain',
      }, { x: 25, y: 25, width: 50, height: 50 });
      toast.success('Sticker added to canvas');
    } finally {
      setInserting(null);
    }
  }

  const isNoKey = apiError?.includes('GIPHY_API_KEY');

  return (
    <>
      <div className="shrink-0" style={{ borderBottom: '1px solid var(--t-border)' }}>
        {/* Search */}
        <form onSubmit={handleSearch} className="p-2 pb-1.5">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} />
            <input
              type="text"
              value={draftQuery}
              onChange={(e) => { setDraftQuery(e.target.value); setActiveCategory(''); }}
              placeholder="Search business stickers…"
              className="input-field pl-6 pr-7 text-xs py-1"
            />
            {draftQuery
              ? <button type="button" onClick={() => { setDraftQuery('business'); setPage(1); setQuery('business'); setActiveCategory('Business'); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X size={11} style={{ color: 'var(--t-muted)' }} />
                </button>
              : <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Search size={11} style={{ color: 'var(--t-muted)' }} />
                </button>}
          </div>
        </form>
        {/* Category chips */}
        <div className="flex flex-wrap gap-1 px-2 pb-2">
          {STICKER_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategory(cat)}
              className="px-2 py-0.5 text-[10px] rounded-full transition-colors"
              style={activeCategory === cat
                ? { background: 'var(--brand-primary)', color: '#fff' }
                : { background: 'var(--t-panel2)', color: 'var(--t-muted)' }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isNoKey ? (
          <ApiKeyBanner
            service="Giphy"
            envVar="GIPHY_API_KEY"
            docsUrl="https://developers.giphy.com"
            docsLabel="developers.giphy.com"
          />
        ) : apiError ? (
          <div className="m-2 p-3 rounded-lg border text-xs" style={{ borderColor: 'var(--t-border)', color: 'var(--t-muted)' }}>
            {apiError}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={18} className="animate-spin text-indigo-400" />
          </div>
        ) : gifs.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-2xl mb-2">✨</p>
            <p className="text-xs" style={{ color: 'var(--t-muted)' }}>No stickers found</p>
          </div>
        ) : (
          <>
            <div className="p-2 grid grid-cols-2 gap-1.5">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => handleInsert(gif)}
                  disabled={inserting === gif.id}
                  className="relative rounded-lg overflow-hidden hover:ring-2 hover:ring-indigo-500 transition-all group cursor-pointer"
                  style={{
                    aspectRatio: '1/1',
                    backgroundImage: 'repeating-conic-gradient(var(--t-panel) 0% 25%, var(--t-panel2) 0% 50%)',
                    backgroundSize: '12px 12px',
                  }}
                  title={gif.title}
                >
                  <img src={gif.thumb} alt={gif.title} className="w-full h-full object-contain" loading="lazy" />
                  {inserting === gif.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Loader2 size={16} className="animate-spin text-white" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-1"
                       style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                    <p className="text-white text-[9px] truncate">{gif.title}</p>
                  </div>
                </button>
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => p + 1)} />
            <div className="px-2 pb-2 shrink-0">
              <a href="https://giphy.com" target="_blank" rel="noreferrer"
                 className="flex items-center gap-1 text-xs hover:underline"
                 style={{ color: 'var(--t-muted)' }}
                 onClick={(e) => e.stopPropagation()}>
                <ExternalLink size={10} />
                Powered by GIPHY
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Shared Giphy browser (GIFs or Stickers) ───────────────────

function GiphyBrowser({ mode }) {
  const [gifs, setGifs] = useState([]);
  const [query, setQuery] = useState('');
  const [draftQuery, setDraftQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [inserting, setInserting] = useState(null);

  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  const fetchGifs = useCallback(async (q, p) => {
    setLoading(true);
    setApiError(null);
    try {
      const result = mode === 'gifs'
        ? (q ? await giphyApi.searchGifs(q, p) : await giphyApi.trendingGifs(p))
        : (q ? await giphyApi.searchStickers(q, p) : await giphyApi.trendingStickers(p));
      setGifs(result.gifs);
      setTotalPages(result.totalPages);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    setQuery('');
    setDraftQuery('');
    setPage(1);
  }, [mode]);

  useEffect(() => { fetchGifs(query, page); }, [query, page, fetchGifs]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
  }

  function handleInsert(gif) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    setInserting(gif.id);
    try {
      const isSticker = mode === 'stickers';
      addLayer(activePageId, {
        type: 'image',
        src: gif.url,
        alt: gif.title,
        objectFit: isSticker ? 'contain' : 'cover',
      }, isSticker
        ? { x: 25, y: 25, width: 50, height: 50 }
        : { x: 0, y: 0, width: 100, height: 60 });
      toast.success(`${isSticker ? 'Sticker' : 'GIF'} added to canvas`);
    } finally {
      setInserting(null);
    }
  }

  const isNoKey = apiError?.includes('GIPHY_API_KEY');
  const isStickers = mode === 'stickers';

  return (
    <>
      <form onSubmit={handleSearch} className="p-2 shrink-0" style={{ borderBottom: '1px solid var(--t-border)' }}>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} />
          <input
            type="text"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            placeholder={`Search ${mode}…`}
            className="input-field pl-6 pr-7 text-xs py-1"
          />
          {draftQuery
            ? <button type="button" onClick={() => { setDraftQuery(''); setPage(1); setQuery(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2">
                <X size={11} style={{ color: 'var(--t-muted)' }} />
              </button>
            : <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
                <Search size={11} style={{ color: 'var(--t-muted)' }} />
              </button>}
        </div>
      </form>

      <div className="flex-1 overflow-y-auto">
        {isNoKey ? (
          <ApiKeyBanner
            service="Giphy"
            envVar="GIPHY_API_KEY"
            docsUrl="https://developers.giphy.com"
            docsLabel="developers.giphy.com"
          />
        ) : apiError ? (
          <div className="m-2 p-3 rounded-lg border text-xs" style={{ borderColor: 'var(--t-border)', color: 'var(--t-muted)' }}>
            {apiError}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={18} className="animate-spin text-indigo-400" />
          </div>
        ) : gifs.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-2xl mb-2">{isStickers ? '✨' : '🎞️'}</p>
            <p className="text-xs" style={{ color: 'var(--t-muted)' }}>No {mode} found</p>
          </div>
        ) : (
          <>
            <div className="p-2 grid grid-cols-2 gap-1.5">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => handleInsert(gif)}
                  disabled={inserting === gif.id}
                  className="relative rounded-lg overflow-hidden hover:ring-2 hover:ring-indigo-500 transition-all group cursor-pointer"
                  style={{
                    aspectRatio: '1/1',
                    backgroundImage: isStickers
                      ? 'repeating-conic-gradient(var(--t-panel) 0% 25%, var(--t-panel2) 0% 50%)'
                      : undefined,
                    backgroundSize: isStickers ? '12px 12px' : undefined,
                    background: isStickers ? undefined : 'var(--t-panel)',
                  }}
                  title={gif.title}
                >
                  <img
                    src={gif.thumb}
                    alt={gif.title}
                    className="w-full h-full"
                    style={{ objectFit: isStickers ? 'contain' : 'cover' }}
                    loading="lazy"
                  />
                  {inserting === gif.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Loader2 size={16} className="animate-spin text-white" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-1"
                       style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                    <p className="text-white text-[9px] truncate">{gif.title}</p>
                  </div>
                </button>
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => p + 1)} />
            <div className="px-2 pb-2 shrink-0">
              <a href="https://giphy.com" target="_blank" rel="noreferrer"
                 className="flex items-center gap-1 text-xs hover:underline"
                 style={{ color: 'var(--t-muted)' }}
                 onClick={(e) => e.stopPropagation()}>
                <ExternalLink size={10} />
                Powered by GIPHY
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Crawl tab ────────────────────────────────────────────────

function CrawlTab() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);
  const updatePageBackground = useEditorStore((s) => s.updatePageBackground);

  async function handleCrawl(e) {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await crawlerApi.extract(trimmed);
      setResult(data);
      toast.success(`Crawled! ${data.images.length} images found.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Crawl failed');
    } finally {
      setLoading(false);
    }
  }

  function handleInsertImage(image) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    addLayer(activePageId, { type: 'image', src: image.url, alt: image.alt, objectFit: 'cover' }, { x: 0, y: 0, width: 100, height: 60 });
    toast.success('Image added to canvas');
  }

  function handleSetBackground(image) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    updatePageBackground(activePageId, { type: 'image', mediaSrc: image.url });
    toast.success('Background updated');
  }

  function handleCopyColor(color) {
    navigator.clipboard.writeText(color).then(() => toast.success(`Copied ${color}`));
  }

  function handleInsertLogo(logoUrl) {
    if (!activePageId) { toast.error('Select a page first'); return; }
    addLayer(activePageId, { type: 'image', src: logoUrl, alt: 'Logo', objectFit: 'contain' }, { x: 5, y: 5, width: 30, height: 15 });
    toast.success('Logo added to canvas');
  }

  const typeBadgeColor = {
    hero: 'var(--brand-primary)',
    product: '#ec4899',
    logo: '#10b981',
    general: '#f59e0b',
  };

  return (
    <>
      <form onSubmit={handleCrawl} className="p-2 shrink-0" style={{ borderBottom: '1px solid var(--t-border)' }}>
        <p className="text-[10px] mb-1.5" style={{ color: 'var(--t-muted)' }}>
          Import brand assets from any website
        </p>
        <div className="flex gap-1.5">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="input-field text-xs py-1 flex-1"
            required
          />
          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="px-3 py-1 text-xs font-medium rounded-md transition-colors shrink-0 disabled:opacity-40"
            style={{ background: 'var(--brand-primary)', color: '#fff' }}
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />}
          </button>
        </div>
      </form>

      <div className="flex-1 overflow-y-auto p-2">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Loader2 size={18} className="animate-spin text-indigo-400" />
            <p className="text-[10px]" style={{ color: 'var(--t-muted)' }}>Analyzing website & saving assets...</p>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-lg border text-xs" style={{ borderColor: '#ef444450', background: '#ef444410', color: '#f87171' }}>
            {error}
          </div>
        )}

        {result && !loading && (
          <div className="space-y-3">
            {/* Site info */}
            <div className="p-2 rounded-lg" style={{ background: 'var(--t-panel)' }}>
              <div className="flex items-center gap-2 mb-1">
                {result.metadata.favicon && <img src={result.metadata.favicon} alt="" className="w-4 h-4 rounded" />}
                <p className="text-xs font-semibold truncate" style={{ color: 'var(--t-text)' }}>{result.metadata.siteName || result.metadata.title}</p>
              </div>
              {result.metadata.description && (
                <p className="text-[10px] line-clamp-2" style={{ color: 'var(--t-muted)' }}>{result.metadata.description}</p>
              )}
            </div>

            {/* Logo */}
            {result.logo && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--t-muted)' }}>Logo</p>
                <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'var(--t-panel)' }}>
                  <img src={result.logo} alt="Logo" className="h-8 w-auto object-contain rounded" style={{ maxWidth: '80px' }} />
                  <button onClick={() => handleInsertLogo(result.logo)}
                    className="px-2 py-0.5 text-[10px] font-medium rounded transition-colors ml-auto"
                    style={{ background: 'var(--brand-primary)', color: '#fff' }}>
                    Insert
                  </button>
                </div>
              </div>
            )}

            {/* Brand Colors */}
            {result.colors.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--t-muted)' }}>Brand Colors</p>
                <div className="flex flex-wrap gap-1.5">
                  {result.colors.map((color, i) => (
                    <button key={i} onClick={() => handleCopyColor(color)}
                      className="w-8 h-8 rounded-lg border transition-transform hover:scale-110 relative group"
                      style={{ background: color, borderColor: 'var(--t-border)' }}
                      title={`${color} — click to copy`}>
                      <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[8px] opacity-0 group-hover:opacity-100 whitespace-nowrap"
                            style={{ color: 'var(--t-muted)' }}>{color}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Fonts */}
            {result.fonts.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--t-muted)' }}>Brand Fonts</p>
                <div className="flex flex-wrap gap-1">
                  {result.fonts.map((font, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{ background: 'var(--t-panel)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}>
                      {font}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Images grid */}
            {result.images.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--t-muted)' }}>
                  Images ({result.images.length}) — saved to media library
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {result.images.map((image, i) => (
                    <div key={i} className="relative rounded-lg overflow-hidden group"
                      style={{ aspectRatio: '1/1', background: 'var(--t-panel)' }}>
                      <img src={image.url} alt={image.alt} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-semibold capitalize"
                        style={{ background: typeBadgeColor[image.type] || '#6b7280', color: '#fff', backdropFilter: 'blur(4px)' }}>
                        {image.type}
                      </div>
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5"
                           style={{ background: 'rgba(0,0,0,0.55)' }}>
                        <button onClick={() => handleInsertImage(image)}
                          className="px-3 py-1 text-[10px] font-medium rounded transition-colors"
                          style={{ background: 'var(--brand-primary)', color: '#fff' }}>Insert</button>
                        {(image.type === 'hero' || image.type === 'product') && (
                          <button onClick={() => handleSetBackground(image)}
                            className="px-3 py-1 text-[10px] font-medium rounded transition-colors"
                            style={{ background: '#ec4899', color: '#fff' }}>Set as BG</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && !error && !result && (
          <div className="text-center py-10">
            <Globe size={22} className="mx-auto mb-2" style={{ color: 'var(--t-muted)' }} />
            <p className="text-xs mb-1" style={{ color: 'var(--t-muted)' }}>Import from Website</p>
            <p className="text-[10px]" style={{ color: 'var(--t-muted)' }}>
              Extracts images, logo, colors & fonts.<br/>Assets are saved to your media library.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

// ── Shared components ─────────────────────────────────────────

function ApiKeyBanner({ service, envVar, docsUrl, docsLabel }) {
  return (
    <div className="m-2 p-3 rounded-lg border" style={{ borderColor: '#f59e0b50', background: '#f59e0b08' }}>
      <div className="flex gap-2 mb-2">
        <Key size={14} className="text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs font-semibold text-amber-500">{service} API key required</p>
      </div>
      <p className="text-xs mb-2" style={{ color: 'var(--t-muted)' }}>
        Get a free key at{' '}
        <a href={docsUrl} target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline">
          {docsLabel}
        </a>
        , then add it to <code className="text-xs px-1 rounded" style={{ background: 'var(--t-panel2)' }}>backend/.env</code>:
      </p>
      <code className="block text-xs px-2 py-1.5 rounded select-all"
            style={{ background: 'var(--t-panel2)', color: 'var(--t-text2)' }}>
        {envVar}=your_key_here
      </code>
      <p className="text-xs mt-2" style={{ color: 'var(--t-muted)' }}>Restart the backend after saving.</p>
    </div>
  );
}

function Pagination({ page, totalPages, onPrev, onNext }) {
  return (
    <div className="flex items-center justify-between px-2 pb-3 shrink-0">
      <button
        onClick={onPrev}
        disabled={page <= 1}
        className="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors disabled:opacity-30"
        style={{ background: 'var(--t-panel)', color: 'var(--t-text2)' }}
      >
        <ChevronLeft size={12} /> Prev
      </button>
      <span className="text-xs" style={{ color: 'var(--t-muted)' }}>
        p.{page}{totalPages ? `/${totalPages}` : ''}
      </span>
      <button
        onClick={onNext}
        disabled={totalPages !== null && page >= totalPages}
        className="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors disabled:opacity-30"
        style={{ background: 'var(--t-panel)', color: 'var(--t-text2)' }}
      >
        Next <ChevronRight size={12} />
      </button>
    </div>
  );
}
